import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { useUsers } from "@/contexts/users-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { ActionDTO } from "@shaarp/common";
import { useEffect, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
} from "recharts";
import { TeamChartLegendItem } from "./team-chart-legend-item";

type TeamActionsChartProps = {
  data: ActionDTO[];
  userColors: Map<string, string>;
  isLoading: boolean;
};

export function TeamActionsChart({
  data,
  userColors,
  isLoading,
}: TeamActionsChartProps) {
  const { t } = useTranslation();
  const { users, apiFetchUsers } = useUsers();

  useEffect(() => {
    apiFetchUsers();
  }, []);

  const userIds = useMemo(() => {
    const ids = new Set<string>();
    for (const actionData of data) {
      for (const userAction of actionData.users) {
        ids.add(userAction.userId);
      }
    }
    return Array.from(ids);
  }, [data]);

  const chartData = useMemo(() => {
    return data.map((actionData) => {
      const entry: Record<string, string | number> = {
        action: t(`team.actions.types.${actionData.action}`, {
          defaultValue: actionData.action,
        }),
      };

      let total = 0;
      for (const userAction of actionData.users) {
        entry[userAction.userId] = userAction.count;
        total += userAction.count;
      }
      entry.total = total;

      return entry;
    });
  }, [data, t]);

  const chartConfig = useMemo(() => {
    const config: ChartConfig = {};

    userIds.forEach((userId) => {
      const user = users.find((u) => u.id === userId);
      config[userId] = {
        label: user?.fullName ?? userId,
        color: userColors.get(userId) ?? "hsl(250, 97%, 62%)",
      };
    });

    return config;
  }, [userIds, users, userColors]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[250px]">
        <div className="animate-pulse text-muted-foreground">
          {t("common.loading")}
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[250px]">
        <p className="text-muted-foreground">{t("team.actions.noData")}</p>
      </div>
    );
  }

  return (
    <>
      <ChartContainer config={chartConfig} className="h-[250px] w-full">
        <BarChart data={chartData} accessibilityLayer>
          <CartesianGrid vertical={false} syncWithTicks={true} />
          <XAxis
            dataKey="action"
            tickLine={false}
            tickMargin={10}
            axisLine={false}
          />
          <YAxis
            tickLine={true}
            axisLine={false}
            tickMargin={8}
            padding={{ top: 10 }}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          {userIds.map((userId, index) => {
            const isLast = index === userIds.length - 1;
            const color = userColors.get(userId) ?? "hsl(250, 97%, 62%)";
            return (
              <Bar
                key={userId}
                dataKey={userId}
                stackId="a"
                fill={color}
                radius={isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              >
                {/* Display the total for that action on top of the bar */}
                {isLast && (
                  <LabelList
                    dataKey="total"
                    position="top"
                    fontSize={12}
                    fontWeight={500}
                  />
                )}
              </Bar>
            );
          })}
        </BarChart>
      </ChartContainer>
      <ul className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-1.5 text-xs">
        {userIds.map((userId) => (
          <li key={userId}>
            <TeamChartLegendItem
              color={userColors.get(userId) ?? "hsl(250, 97%, 62%)"}
              name={users.find((u) => u.id === userId)?.fullName ?? userId}
            />
          </li>
        ))}
      </ul>
    </>
  );
}
